// SettingsFrame.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Checkbox,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSettings,
  addKeyword,
  deleteKeywords,
  activateKeywords,
  deactivateKeywords,
  addNhs,
  deleteNhs,
  activateNhs,
  deactivateNhs,
  clearError,
} from "../reducers/settingsSlice";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const SettingsFrame = () => {
  const dispatch = useDispatch();
  const { keywords, nhsNumbers, error } = useSelector((s) => s.settings || {});
  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const [tab, setTab] = useState(0);
  const [newKeyword, setNewKeyword] = useState("");
  const [newNhs, setNewNhs] = useState("");
  const [newNhsReason, setNewNhsReason] = useState("");

  const [selectedKeywords, setSelectedKeywords] = useState({
    ids: new Set(),
    type: "include",
  });

  const [selectedNhs, setSelectedNhs] = useState({
    ids: new Set(),
    type: "include",
  });

  // Load settings
  useEffect(() => {
    if (license_key) dispatch(fetchSettings({ license_key }));
  }, [dispatch, license_key]);

  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  // Show errors
  useEffect(() => {
    if (error) {
      alert("Settings error: " + error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Keyword rows
  const keywordRows = useMemo(
    () =>
      (keywords || []).map((k, i) => ({
        id: `kw-${i}`,
        keyword: k.keyword,
        enabled: k.enabled,
      })),
    [keywords],
  );

  // NHS rows
  const nhsRows = useMemo(
    () =>
      (nhsNumbers || []).map((n, i) => ({
        id: `nhs-${i}`,
        nhs_number: n.nhs_number,
        reason: n.reason || "",
        enabled: n.enabled,
      })),
    [nhsNumbers],
  );

  // Keyword columns
  const keywordColumns = [
    {
      field: "keyword",
      headerName: "Keyword",
      flex: 1,
    },
    {
      field: "enabled",
      headerName: "Enabled",
      width: 120,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.enabled}
          onClick={(event) => event.stopPropagation()} // prevent row selection
          onChange={async (e) => {
            const kw = params.row.keyword;
            if (e.target.checked)
              await dispatch(activateKeywords({ keywords: [kw], license_key }));
            else
              await dispatch(
                deactivateKeywords({ keywords: [kw], license_key }),
              );
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={async () => {
                await dispatch(
                  deleteKeywords({
                    keywords: [params.row.keyword],
                    license_key,
                  }),
                );
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  // NHS columns
  const nhsColumns = [
    { field: "nhs_number", headerName: "NHS Number", flex: 1 },
    { field: "reason", headerName: "Reason", flex: 1 },
    {
      field: "enabled",
      headerName: "Enabled",
      width: 120,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.enabled}
          onClick={(event) => event.stopPropagation()} // prevent row selection
          onChange={async (e) => {
            const n = params.row.nhs_number;
            if (e.target.checked)
              await dispatch(activateNhs({ nhs_numbers: [n], license_key }));
            else
              await dispatch(deactivateNhs({ nhs_numbers: [n], license_key }));
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={async () => {
              await dispatch(
                deleteNhs({
                  nhs_numbers: [params.row.nhs_number],
                  license_key,
                }),
              );
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Paper
      elevation={2}
      sx={{ p: 2, height: "90vh", display: "flex", flexDirection: "column" }}
    >
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        aria-label="settings tabs"
      >
        <Tab label="Inconclusive keywords" id="settings-tab-0" />
        <Tab label="Skip Patients" id="settings-tab-1" />
      </Tabs>

      {/* Keywords */}
      <TabPanel
        value={tab}
        index={0}
        sx={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            label="Add keyword"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            placeholder="Add a keyword"
            size="small"
            onKeyDown={(e) => {
              if (e.key === "Enter" && newKeyword.trim()) {
                dispatch(
                  addKeyword({ keyword: newKeyword.trim(), license_key }),
                );
                setNewKeyword("");
              }
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!has_write}
            onClick={() => {
              if (!newKeyword.trim()) return;
              dispatch(addKeyword({ keyword: newKeyword.trim(), license_key }));
              setNewKeyword("");
            }}
          >
            Add
          </Button>

          <Button
            variant="contained"
            color="error"
            disabled={!has_write}
            onClick={async () => {
              const selectedIds = Array.from(selectedKeywords.ids || new Set());
              if (!selectedIds.length) return;

              const kws = selectedIds
                .map((id) => keywordRows.find((r) => r.id === id)?.keyword)
                .filter(Boolean);

              if (!kws.length) return;

              await dispatch(deleteKeywords({ keywords: kws, license_key }));

              setSelectedKeywords({ ids: new Set(), type: "include" });
            }}
          >
            Delete Selected
          </Button>
        </Stack>

        <div style={{ flex: 1, width: "100%" }}>
          <DataGrid
            rowHeight={45}
            rows={keywordRows}
            columns={keywordColumns}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectedKeywords}
            onRowSelectionModelChange={setSelectedKeywords}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            sx={{
              flex: 1,
              "& .MuiDataGrid-columnHeaders": {
                marginBottom: "20px",
              },
            }}
          />
        </div>
      </TabPanel>

      {/* NHS */}
      <TabPanel
        value={tab}
        index={1}
        sx={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            label="NHS Number"
            value={newNhs}
            onChange={(e) => setNewNhs(e.target.value)}
            size="small"
            sx={{ width: 220 }}
          />
          <TextField
            label="Reason"
            value={newNhsReason}
            onChange={(e) => setNewNhsReason(e.target.value)}
            size="small"
            sx={{ width: 320 }}
          />
          <Button
            variant="contained"
            disabled={!has_write}
            startIcon={<AddIcon />}
            onClick={() => {
              if (!newNhs.trim()) return;
              dispatch(
                addNhs({
                  nhs_number: newNhs.trim(),
                  reason: newNhsReason.trim(),
                  license_key,
                }),
              );
              setNewNhs("");
              setNewNhsReason("");
            }}
          >
            Add
          </Button>
          <Button
            variant="contained"
            disabled={!has_write}
            color="error"
            onClick={async () => {
              const selectedIds = Array.from(selectedNhs.ids || new Set());
              if (!selectedIds.length) return;
              const nums = selectedIds
                .map((id) => nhsRows.find((r) => r.id === id)?.nhs_number)
                .filter(Boolean);

              if (!nums.length) return;

              await dispatch(deleteNhs({ nhs_numbers: nums, license_key }));

              setSelectedNhs({ ids: new Set(), type: "include" });
            }}
          >
            Delete Selected
          </Button>
        </Stack>

        <div style={{ flex: 1, width: "100%" }}>
          <DataGrid
            rowHeight={45}
            rows={nhsRows}
            columns={nhsColumns}
            checkboxSelection
            rowSelectionModel={selectedNhs}
            onRowSelectionModelChange={setSelectedNhs}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            sx={{
              flex: 1,
              "& .MuiDataGrid-columnHeaders": {
                marginBottom: "20px",
              },
            }}
          />
        </div>
      </TabPanel>
    </Paper>
  );
};

export default SettingsFrame;
